// apps/client/src/pages/reports/SharedSuccessionReadinessPage.tsx
// PUBLIC SHARED SUCCESSION READINESS REPORT - Read-only view with VALUTTEN branding

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  Loader2, AlertCircle, Shield, ArrowLeft, Calendar,
  CheckCircle2, Lock, Users, Database, TrendingUp,
  Building2, Clock, BarChart3, Vault,
} from 'lucide-react';
import { formatCurrencyShorthand } from '../../utils/formatters';

type ChecklistItemStatus = 'automated' | 'manual_pending' | 'manual_completed';

interface ChecklistItem {
  id: number;
  label: string;
  status: ChecklistItemStatus;
  weight: number;
  value: string | number | null;
  detail?: any;
}

interface LenderMixEntry {
  lender: string;
  loanCount: number;
  percentage: number;
}

interface AgeDistribution {
  bucket: string;
  count: number;
  percentage: number;
}

interface SuccessionReadinessData {
  loansSettledLast2Years: number;
  lenderMix: LenderMixEntry[];
  loanAgeDistribution: AgeDistribution[];
  activeClientsOnTrail: number;
  totalDatabase: number;
  totalLoansUnderManagement: number;
  loanBookCents?: number | null;
  actualTrailing12mCents?: number;
  latestCompleteMonth?: string | null;
  loansNowVs12MonthsAgo: {
    current: number;
    previous: number;
    growthPercent: number | null;
  };
  readinessScore: number;
  automatedItemsScore: number;
  manualItemsScore: number;
  checklist: ChecklistItem[];
}

interface ShareData {
  shareId: string;
  companyName: string;
  createdAt: string;
  data: SuccessionReadinessData;
}

const SharedSuccessionReadinessPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No share token provided');
        setIsLoading(false);
        return;
      }

      try {
        const validateFunc = httpsCallable<{ token: string }, ShareData>(
          functions,
          'validateSuccessionShareToken'
        );
        const response = await validateFunc({ token });
        setShareData(response.data);
      } catch (err: any) {
        console.error('Failed to validate share token:', err);
        if (err.code === 'functions/permission-denied') {
          setError('This share link is invalid or has expired');
        } else if (err.code === 'functions/not-found') {
          setError('Succession readiness report not found');
        } else {
          setError(err.message || 'Failed to load shared report');
        }
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg">Loading succession readiness report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-red-500/30 text-center">
          <div className="p-4 bg-red-500/20 rounded-full w-fit mx-auto mb-4">
            <AlertCircle className="h-10 w-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Report</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (!shareData) return null;

  const { data } = shareData;
  const createdDate = new Date(shareData.createdAt);
  const formattedDate = createdDate.toLocaleDateString('en-AU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const automatedItems = data.checklist.filter((i) => i.status === 'automated');
  const manualItems = data.checklist.filter((i) => i.status !== 'automated');

  // Score ring colors
  const getScoreColor = (s: number) => {
    if (s >= 80) return 'text-green-400';
    if (s >= 60) return 'text-blue-400';
    if (s >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStrokeColor = (s: number) => {
    if (s >= 80) return 'stroke-green-400';
    if (s >= 60) return 'stroke-blue-400';
    if (s >= 40) return 'stroke-yellow-400';
    return 'stroke-red-400';
  };

  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (data.readinessScore / 100) * circumference;

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="p-8 max-w-5xl mx-auto">
        {/* Header with VALUTTEN Branding */}
        <div className="rounded-2xl p-8 mb-8 bg-gradient-to-r from-emerald-600 via-emerald-700 to-teal-600 shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-4 -right-4 w-64 h-64 bg-white/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-4 -left-4 w-48 h-48 bg-white/20 rounded-full blur-2xl animate-pulse delay-700" />
          </div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="w-10 h-10 text-white" />
              <h1 className="text-4xl font-bold text-white">
                Succession Readiness Report
              </h1>
            </div>
            <p className="text-white/90 text-lg mb-2">{shareData.companyName}</p>
            <div className="flex flex-wrap gap-3 mt-4">
              <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm text-white/90">
                Shared Report - Read Only
              </div>
              <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm text-white/90">
                Powered by VALUTTEN
              </div>
            </div>
          </div>
        </div>

        {/* Report Metadata */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Calendar className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Report Generated</p>
                <p className="text-white font-medium">{formattedDate}</p>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="relative inline-flex items-center justify-center">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r={radius} fill="none" stroke="currentColor" className="text-slate-700" strokeWidth="8" />
                  <circle cx="60" cy="60" r={radius} fill="none" strokeWidth="8" className={getStrokeColor(data.readinessScore)} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" />
                </svg>
                <div className="absolute flex flex-col items-center">
                  <span className={`text-2xl font-bold ${getScoreColor(data.readinessScore)}`}>{data.readinessScore}</span>
                  <span className="text-[10px] text-slate-400">/ 100</span>
                </div>
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-400">Checklist Completion</p>
              <p className="text-white font-medium text-lg">
                {automatedItems.filter((i) => i.value !== null).length} of {data.checklist.length} items
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-500/20 rounded-lg"><Users className="w-5 h-5 text-blue-400" /></div>
              <h3 className="text-slate-400 text-sm">Active Clients on Trail</h3>
            </div>
            <p className="text-3xl font-bold text-white">{data.activeClientsOnTrail.toLocaleString()}</p>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-500/20 rounded-lg"><Vault className="w-5 h-5 text-amber-400" /></div>
              <h3 className="text-slate-400 text-sm">Loans Under Management</h3>
            </div>
            <p className="text-3xl font-bold text-white">{data.totalLoansUnderManagement.toLocaleString()}</p>
            {typeof data.loanBookCents === 'number' && data.loanBookCents > 0 && (
              <p className="text-sm text-slate-400 mt-1">
                {formatCurrencyShorthand(data.loanBookCents)} book value
              </p>
            )}
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-500/20 rounded-lg"><Database className="w-5 h-5 text-purple-400" /></div>
              <h3 className="text-slate-400 text-sm">Total Database</h3>
            </div>
            <p className="text-3xl font-bold text-white">{data.totalDatabase.toLocaleString()}</p>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-500/20 rounded-lg"><TrendingUp className="w-5 h-5 text-green-400" /></div>
              <h3 className="text-slate-400 text-sm">Settled (Last 2 Years)</h3>
            </div>
            <p className="text-3xl font-bold text-white">{data.loansSettledLast2Years.toLocaleString()}</p>
          </div>
        </div>

        {/* Lender Mix */}
        {data.lenderMix.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
            <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Lender Diversification
            </h3>
            <div className="space-y-2">
              {data.lenderMix.slice(0, 10).map((lender) => (
                <div key={lender.lender} className="flex items-center gap-3">
                  <span className="text-sm text-slate-300 w-40 truncate">{lender.lender}</span>
                  <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(lender.percentage, 100)}%` }} />
                  </div>
                  <span className="text-sm text-slate-400 w-20 text-right">{lender.loanCount} ({lender.percentage}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Loan Age Distribution */}
        {data.loanAgeDistribution.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
            <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Loan Maturity Distribution
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {data.loanAgeDistribution.map((bucket) => (
                <div key={bucket.bucket} className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-white">{bucket.count.toLocaleString()}</p>
                  <p className="text-sm text-slate-400">{bucket.bucket}</p>
                  <p className="text-xs text-slate-500">{bucket.percentage}%</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* YoY Growth */}
        {data.loansNowVs12MonthsAgo.growthPercent !== null && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-8">
            <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Book Growth (12 months)
            </h3>
            <div className="flex items-baseline gap-4">
              <span className={`text-3xl font-bold ${data.loansNowVs12MonthsAgo.growthPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {data.loansNowVs12MonthsAgo.growthPercent > 0 ? '+' : ''}{data.loansNowVs12MonthsAgo.growthPercent}%
              </span>
              <span className="text-sm text-slate-400">
                {data.loansNowVs12MonthsAgo.previous.toLocaleString()} → {data.loansNowVs12MonthsAgo.current.toLocaleString()} loans
              </span>
            </div>
          </div>
        )}

        {/* Full Checklist */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-white mb-4">Succession Readiness Checklist</h3>

          <h4 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Verified from Commission Data
          </h4>
          <div className="flex flex-col gap-2 mb-6">
            {automatedItems.map((item) => (
              <div key={item.id} className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="text-xs font-mono text-slate-500 w-6 shrink-0">#{item.id}</span>
                  <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                  <span className="text-sm font-medium text-white truncate">{item.label}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-4">
                  <span className="text-base font-semibold text-white">
                    {item.value !== null ? (typeof item.value === 'number' ? item.value.toLocaleString() : item.value) : '—'}
                  </span>
                  <span className="text-[0.625rem] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400">Auto</span>
                </div>
              </div>
            ))}
          </div>

          <h4 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
            <Lock className="w-4 h-4" /> Additional Profile Data
          </h4>
          <div className="flex flex-col gap-2">
            {manualItems.map((item) => (
              <div key={item.id} className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 bg-slate-800/30 rounded-lg border border-slate-700/30">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="text-xs font-mono text-slate-600 w-6 shrink-0">#{item.id}</span>
                  <Lock className="w-4 h-4 text-slate-600 shrink-0" />
                  <span className="text-sm font-medium text-slate-500 truncate">{item.label}</span>
                </div>
                <span className="text-sm text-slate-600 italic shrink-0 ml-4">Pending</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-6 text-slate-500 text-sm border-t border-slate-700/50">
          <p>This is a shared, read-only view of the succession readiness report.</p>
          <p className="mt-2">
            <a
              href="https://valutten.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:text-emerald-300 font-medium"
            >
              Powered by VALUTTEN
            </a>
            {' '}&bull;{' '}
            Commission tracking and analytics for Australian mortgage brokers
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedSuccessionReadinessPage;
